import React, { useCallback, useMemo } from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';

import { useFulfillmentPlannerContext } from '../../../fulfillment-planner/context';
import {
  deliveryOptions,
  labelOptions,
  marketplaceOptions,
  movementIndicator,
  notificationOptions,
  shipmentOptions,
  externalFulfillmentOptions,
  windsorFrameworkOptions,
  serviceLevelOptions,
} from '../../../services/other-shipping-options/constants';
import { OtherShippingOptionsService } from '../../../services/other-shipping-options/other-shipping-options-service';
import { useOtherShippingOptionsStyleContext } from '../style-provider';
import { OtherShippingOptionsFormSection } from './form-section';
import { getShouldShowBillingAccount } from './inputs/bill-to-account/bill-to-account';
import { messages } from './messages';
import { ShippingAccountContainer } from './shipping-account-container';
import { BULK_UPDATE_FULFILLMENT_PLAN_ID } from '../constants';
import style from './other-shipping-options-form.module.css';
import { WindsorFrameworkPopover } from './popovers/windsor-framework-popover';
import { ServiceLevelPopover } from './popovers/service-level-popover';
import { CustomsReason } from '../../../constants/customs';
import { emptyObject } from 'ui-components/default-props/empty';

export const OtherShippingOptionsForm = ({
  fulfillmentPlanId = '',
  onOptionUpdated = noop,
  onInputUpdated = noop,
  optionData = emptyObject,
  onOptionChecked = noop,
  sellerDefaultCurrency,
  sectionsToShow,
  sellerLabelProviderId: sellerLabelProviderIdProp = null,
}) => {
  const { classes } = useOtherShippingOptionsStyleContext();
  const fulfillmentPlannerContext = useFulfillmentPlannerContext();

  // context is not present in the bulk update modal so be defensive
  const { onSellerLabelProviderChange, serviceIdAndSellerLabelProviderId = {} } = fulfillmentPlannerContext;
  const { serviceId, sellerLabelProviderId: sellerLabelProviderIdContext } = serviceIdAndSellerLabelProviderId;

  // Prefer prop over context for bulk update scenarios
  const sellerLabelProviderId = sellerLabelProviderIdProp ?? sellerLabelProviderIdContext;

  const handleCheck = useCallback(
    (id) => {
      if (optionData?.[id]?.checked) {
        const defaultOptionState = OtherShippingOptionsService.getInitialStateForOption(id, sellerDefaultCurrency);
        if (defaultOptionState !== undefined) {
          onOptionChecked(id, null);
        } else {
          onOptionChecked(id);
        }
      } else {
        onOptionChecked(id, OtherShippingOptionsService.getInitialStateForOption(id, sellerDefaultCurrency));
      }
    },
    [onOptionChecked, optionData, sellerDefaultCurrency],
  );

  const handleBillingAccountCheckForBulkModal = useCallback(() => {
    if (optionData?.billToAccount?.checked) {
      onOptionChecked('billToAccount', { value: null });
    } else {
      onOptionChecked('billToAccount', OtherShippingOptionsService.getInitialStateForOption('billToAccount'));
    }
  }, [onOptionChecked, optionData?.billToAccount?.checked]);

  const shippingOptions = useMemo(() => {
    return OtherShippingOptionsService.getOtherShippingOptions(sectionsToShow);
  }, [sectionsToShow]);

  const isBulkUpdate = fulfillmentPlanId === BULK_UPDATE_FULFILLMENT_PLAN_ID;
  const isLabelProviderSelected = !!sellerLabelProviderId;

  const showBillToAccount = getShouldShowBillingAccount(isBulkUpdate, isLabelProviderSelected);

  const { customsReason } = useFulfillmentPlannerContext();

  const showWindsorFrameworkSection = customsReason === CustomsReason.RequireWindsorDeclaration;
  const showMovementIndicatorSection = customsReason === CustomsReason.RequireMovementIndicator;
  const movementIndicatorSectionOptions = useMemo(
    () => shippingOptions[windsorFrameworkOptions].filter((option) => option.optionName === movementIndicator),
    [shippingOptions],
  );

  return (
    <div className={classnames(style['other-shipping-options-form'], classes.form)}>
      {showBillToAccount ? (
        <div className={style['bill-to-account-section']}>
          <ShippingAccountContainer
            checked={optionData.billToAccount.checked}
            fulfillmentPlanId={fulfillmentPlanId}
            onCheck={handleBillingAccountCheckForBulkModal}
            optionData={optionData.billToAccount.value}
            selectedSellerLabelProviderId={sellerLabelProviderId}
            selectedServiceId={serviceId}
            updateContext={onSellerLabelProviderChange}
            updateInput={onInputUpdated}
            updateOption={onOptionUpdated}
          />
        </div>
      ) : null}

      {showWindsorFrameworkSection ? (
        <OtherShippingOptionsFormSection
          fulfillmentPlanId={fulfillmentPlanId}
          handleCheck={handleCheck}
          onInputUpdated={onInputUpdated}
          onOptionUpdated={onOptionUpdated}
          optionData={optionData}
          sectionOptions={shippingOptions[windsorFrameworkOptions]}
          title={
            <FormattedMessage
              {...messages.windsorFrameworkOptionsTitle}
              values={{
                popover: () => <WindsorFrameworkPopover />,
              }}
            />
          }
        />
      ) : null}

      {showMovementIndicatorSection ? (
        <OtherShippingOptionsFormSection
          fulfillmentPlanId={fulfillmentPlanId}
          handleCheck={handleCheck}
          onInputUpdated={onInputUpdated}
          onOptionUpdated={onOptionUpdated}
          optionData={optionData}
          sectionOptions={movementIndicatorSectionOptions}
          title={<FormattedMessage {...messages.movementIndicatorOptionsTitle} />}
        />
      ) : null}

      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[serviceLevelOptions]}
        sellerLabelProviderId={sellerLabelProviderId}
        title={
          <FormattedMessage
            {...messages.serviceLevelsTitle}
            values={{
              popover: () => <ServiceLevelPopover />,
            }}
          />
        }
      />

      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[shipmentOptions]}
        title={<FormattedMessage {...messages.shipmentOptionsTitle} />}
      />
      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[notificationOptions]}
        title={<FormattedMessage {...messages.notificationOptionsTitle} />}
      />
      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[deliveryOptions]}
        title={<FormattedMessage {...messages.deliveryOptionsTitle} />}
      />
      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[labelOptions]}
        title={<FormattedMessage {...messages.labelOptionsTitle} />}
      />
      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[marketplaceOptions]}
        title={<FormattedMessage {...messages.marketplaceOptionsTitle} />}
      />
      <OtherShippingOptionsFormSection
        fulfillmentPlanId={fulfillmentPlanId}
        handleCheck={handleCheck}
        onInputUpdated={onInputUpdated}
        onOptionUpdated={onOptionUpdated}
        optionData={optionData}
        sectionOptions={shippingOptions[externalFulfillmentOptions]}
        title={<FormattedMessage {...messages.externalFulfillmentOptionsTitle} />}
      />
    </div>
  );
};

OtherShippingOptionsForm.propTypes = {
  /*
  this is structured like
  {
    includeReturnLabel: {
      packageTypeId: 3,
      checked: true,
    }
  }
   */
  optionData: PropTypes.shape({
    billToAccount: PropTypes.shape({
      checked: PropTypes.bool,
      value: PropTypes.shape({
        billToParty: PropTypes.string,
        accountNumber: PropTypes.string,
        billingCountry: PropTypes.string,
        billingPostalCode: PropTypes.string,
      }),
    }),
  }),
  onOptionUpdated: PropTypes.func,
  onOptionChecked: PropTypes.func,
  onInputUpdated: PropTypes.func,
  // needed for include return label,
  fulfillmentPlanId: PropTypes.string,
  sectionsToShow: PropTypes.arrayOf(PropTypes.string).isRequired,
  sellerDefaultCurrency: PropTypes.string.isRequired,
  sellerLabelProviderId: PropTypes.string,
};
